import { Immutable } from "immer";
import React from "react";

import type { ProductCalloutBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { OverlayBannerBlock } from "../OverlayBannerBlock";
import { WistiaBackgroundVideo } from "../WistiaBackgroundVideo";
import { getSpacingClasses } from "../_spacing/spacing";

type IProps = Immutable<{
    value: Value;
}>;

const renderImage = (v: Immutable<Value>): React.JSX.Element | null => {
    const theme = v.theme?.theme;

    if (v.videoID) {
        return (
            <>
                <WistiaBackgroundVideo videoId={v.videoID} />
                {theme === "theme--cloud-product-highlight--full-block" &&
                    v.mobile_image && (
                        <picture>
                            <source
                                media="(max-width: 768px)"
                                srcSet={
                                    v.mobile_image.renditions["width-900"]?.url
                                }
                            />
                            <img
                                src={
                                    v.mobile_image.renditions["width-900"]
                                        ?.url ?? v.mobile_image.url
                                }
                                alt={v.mobile_image.title}
                                loading="lazy"
                                decoding="async"
                            />
                        </picture>
                    )}
            </>
        );
    }

    if (!v.image) {
        return null;
    }

    if (theme === "theme--focal-point") {
        return (
            <picture>
                <source
                    media="(max-width: 578px)"
                    srcSet={v.image.renditions["fill-450x330"]?.url}
                />
                <source
                    media="(min-width: 579px) and (max-width: 1200px)"
                    srcSet={v.image.renditions["width-1200"]?.url}
                />
                <img
                    src={v.image.renditions["width-1600"]?.url ?? v.image.url}
                    alt={v.image.title}
                    loading="lazy"
                    decoding="async"
                />
            </picture>
        );
    }

    if (
        theme === "theme--cloud-product-highlight--full-block" ||
        theme === "theme--cloud-product-highlight--option-block"
    ) {
        return (
            <picture>
                {v.mobile_image && (
                    <source
                        media="(max-width: 768px)"
                        srcSet={v.mobile_image.renditions["width-900"]?.url}
                    />
                )}
                <img
                    src={v.image.renditions["width-1600"]?.url ?? v.image.url}
                    alt={v.image.title}
                    loading="lazy"
                    decoding="async"
                />
            </picture>
        );
    }

    const r = v.image.renditions;
    const srcSet = [500, 1000, 1600]
        .map((w) => {
            const url = r[`width-${w}`]?.url;
            return url ? `${url} ${w}w` : "";
        })
        .filter(Boolean)
        .join(",");
    const primary = r["width-1600"] ?? v.image;

    return (
        <img
            src={primary.url}
            srcSet={srcSet || undefined}
            sizes="auto"
            width={primary.width}
            height={primary.height}
            loading="lazy"
            decoding="async"
            alt={v.image.title}
        />
    );
};

export const ProductCalloutBlock = (
    props: IProps,
): React.JSX.Element | null => {
    const v = props.value;
    return (
        <section
            className={concatClassNames([
                "product-callout-block",
                v.theme?.theme ?? "",
                v.responsive_visibility?.visibility ?? "",
                v.content_alignment ?? "",
                v.text_alignment ?? "",
                v.gradient_option ?? "",
                v.text_style ?? "",
                v.spacing ? getSpacingClasses(v.spacing) : "",
                v.videoID ? "has-video" : "",
            ])}
        >
            {renderImage(v)}
            {/* RichTextBlock already renders a wrapping <div> around the
                `.rich-text` content, matching the legacy template's
                `<div>{% include_block description %}</div>`. An extra wrapper
                here would push `.rich-text` a level deeper, breaking the
                `.product-callout-block > div … .rich-text` flex-centering
                rules (the thelabui <div> becomes the full-width flex item). */}
            <RichTextBlock value={v.description ?? ""} />
            {v.overlay_banner && (
                <OverlayBannerBlock value={v.overlay_banner} />
            )}
        </section>
    );
};
